import { useEffect } from 'react'
import {
  Box,
  Flex,
  HStack,
  NativeSelect,
  Slider,
  Switch,
  Text,
  VStack,
  Field,
  type SliderValueChangeDetails,
} from '@chakra-ui/react';
import { Tooltip } from '@/components/ui/tooltip';
import {
  METRIC_GROUP_LABELS,
  METRIC_OPTIONS,
  UNION_LAYER_OPTIONS,
  UNIT_TYPE_OPTIONS,
  UNIVERSE_METRIC_OPTION,
  US_STATES,
  supportsLiveMetrics,
} from '../../types/geoAnalytics'
import type { GeoUnitTypeKey, MetricGroup, MetricOption } from '../../types/geoAnalytics'
import { familyYears, leanYears, visibleMetricOptions } from './metricFamilies'
import { quadrantRationale } from './bivariate'
// Legend annotates the LIGHT analytics basemap — the constant map palette, not viz tokens.
import { BIVARIATE_PALETTE } from './viz/mapRamps'
import { MetricLegend } from './MetricLegend'
import { LayerModeSwitch } from './LayerModeSwitch'

export type AreaLayerMode = 'single' | 'quadrant' | 'compare'

export interface AreaLayerState {
  unitType: GeoUnitTypeKey
  state: string
  metricOptionKey: string | null
  year: number | null
  opacity: number
  useUniverse: boolean
  mode: AreaLayerMode
  swingEnabled: boolean
  swingFromYear: number | null
  swingToYear: number | null
  quadrantTurnoutYear: number | null
}

export const DEFAULT_AREA_LAYER_STATE: AreaLayerState = {
  // DT-PROD-7(i): precincts, not censusTract. This is the floor of the resolution order
  // (manual pick > committee defaultMapDistrictType > precinct) — see AnalyticsMapPage's
  // state seed. Census tracts are the densest geography and were the wrong thing to open on.
  unitType: 'precinct',
  state: '',
  metricOptionKey: null,
  year: null,
  opacity: 0.7,
  useUniverse: false,
  mode: 'single',
  swingEnabled: false,
  swingFromYear: null,
  swingToYear: null,
  quadrantTurnoutYear: null,
}

export function selectedMetricOption(s: AreaLayerState): MetricOption | null {
  if (s.useUniverse) return UNIVERSE_METRIC_OPTION
  if (!s.metricOptionKey) return null
  return (
    METRIC_OPTIONS.find(o => o.key === s.metricOptionKey)
    ?? UNION_LAYER_OPTIONS.find(o => o.key === s.metricOptionKey)
    ?? null
  )
}

interface AreaLayersPanelProps {
  value: AreaLayerState
  onChange: (next: AreaLayerState) => void
  availableMetricKeys?: string[]
  allowCompare?: boolean
}

const METRIC_GROUP_ORDER: MetricGroup[] = ['elections', 'demographics', 'terrain', 'union']

export function AreaLayersPanel({
  value,
  onChange,
  availableMetricKeys = [],
  allowCompare = true,
}: AreaLayersPanelProps) {
  const option = selectedMetricOption(value)

  const families = familyYears(availableMetricKeys)
  const availableLeanYears = leanYears(availableMetricKeys)
  const turnoutStaticYears = METRIC_OPTIONS.find(o => o.key === 'turnout.general')?.yearOptions ?? []

  const keysLoaded = availableMetricKeys.length > 0
  const hasLean = availableMetricKeys.includes('lean')
  const hasTurnout = availableMetricKeys.some(k => k.startsWith('turnout.general.'))
  const quadrantAvailable = hasLean && hasTurnout
  const quadrantDisabled = keysLoaded && !quadrantAvailable

  // Union footprint/leverage + "my universe" go through the live-metrics endpoint, which serves
  // LIVE_METRIC_UNIT_TYPES only. On ward/municipality they 400 and silently blank the map — so gate them.
  const liveSupported = supportsLiveMetrics(value.unitType)
  const unionSelected = UNION_LAYER_OPTIONS.some(o => o.key === value.metricOptionKey)

  // Auto-reset an active quadrant when the selected geography can't support it (e.g. switch to tracts).
  useEffect(() => {
    if (value.mode === 'quadrant' && quadrantDisabled) onChange({ ...value, mode: 'single' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.mode, quadrantDisabled])

  // Same guard for live-metrics layers: switching to a coarse geography clears a stranded union/universe
  // layer rather than leaving it selected-but-blank.
  useEffect(() => {
    if (liveSupported) return
    if (value.useUniverse) onChange({ ...value, useUniverse: false })
    else if (unionSelected) onChange({ ...value, metricOptionKey: null, year: null })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveSupported, value.useUniverse, unionSelected])

  // Prefer discovery; fall back to the static catalog ONLY before availableMetricKeys loads,
  // so quadrant never offers a turnout year it can't plot.
  const turnoutYears = keysLoaded
    ? (families['turnout.general'] ?? [])
    : (families['turnout.general']?.length ? families['turnout.general'] : turnoutStaticYears)
  const latestTurnoutYear = turnoutYears.length > 0 ? turnoutYears[turnoutYears.length - 1] : null

  const pickerOptions = availableMetricKeys.length > 0
    ? visibleMetricOptions(METRIC_OPTIONS, availableMetricKeys)
    : METRIC_OPTIONS

  // Year list for the selected option: prefer discovery, fall back to the static catalog.
  const optionYears = option?.requiresYear
    ? (families[option.key]?.length ? families[option.key] : option.yearOptions ?? [])
    : []

  return (
    <VStack gap={3} align="stretch" px={1} py={2}>
      <Field.Root>
        <Field.Label fontSize="xs" mb={1}>State</Field.Label>
        <NativeSelect.Root size="sm">
          <NativeSelect.Field
            aria-label="State"
            value={value.state}
            onChange={e => onChange({ ...value, state: e.target.value })}>
            <option value="">Select a state…</option>
            {US_STATES.map(s => (
              <option key={s.code} value={s.code}>{s.name}</option>
            ))}
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
      </Field.Root>

      {!value.state && (
        <Text fontSize="xs" color="gray.500">
          Select a state to begin. (Demo data: Virginia.)
        </Text>
      )}

      <Field.Root>
        <Field.Label fontSize="xs" mb={1}>Geography</Field.Label>
        <NativeSelect.Root size="sm">
          <NativeSelect.Field
            aria-label="Geography"
            value={value.unitType}
            onChange={e => onChange({ ...value, unitType: e.target.value as GeoUnitTypeKey })}>
            {UNIT_TYPE_OPTIONS.map(u => (
              <option key={u.key} value={u.key}>{u.label}</option>
            ))}
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
      </Field.Root>

      {keysLoaded && !hasLean && (
        <Text fontSize="xs" color="gray.500">
          Lean &amp; the Quadrant view needs precinct-level results — not available for this geography.
          Use precincts, counties, or districts for partisan layers.
        </Text>
      )}

      <LayerModeSwitch
        mode={value.mode}
        allowCompare={allowCompare}
        quadrantDisabled={quadrantDisabled}
        quadrantDisabledReason="Quadrant needs lean + turnout — available for precincts, counties, and districts."
        onModeChange={m => {
          if (m === 'quadrant') {
            onChange({ ...value, mode: 'quadrant', quadrantTurnoutYear: value.quadrantTurnoutYear ?? latestTurnoutYear })
          } else {
            onChange({ ...value, mode: m })
          }
        }}
      />

      {value.mode === 'quadrant' ? (
        <>
          <Field.Root>
            <Field.Label fontSize="xs" mb={1}>Turnout year</Field.Label>
            <NativeSelect.Root size="sm">
              <NativeSelect.Field
                aria-label="Turnout year"
                value={value.quadrantTurnoutYear ?? ''}
                onChange={e => onChange({ ...value, quadrantTurnoutYear: Number(e.target.value) })}>
                {turnoutYears.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Field.Root>
          <BivariateLegend />
        </>
      ) : (
        <>
          <Field.Root>
            <Field.Label fontSize="xs" mb={1}>Metric</Field.Label>
            <NativeSelect.Root size="sm">
              <NativeSelect.Field
                aria-label="Metric"
                value={value.metricOptionKey ?? ''}
                onChange={e => {
                  const key = e.target.value || null
                  const opt = key
                    ? METRIC_OPTIONS.find(o => o.key === key) ?? UNION_LAYER_OPTIONS.find(o => o.key === key) ?? null
                    : null
                  const years = key ? families[key] ?? [] : []
                  onChange({
                    ...value,
                    metricOptionKey: key,
                    year: opt?.requiresYear
                      ? (years[years.length - 1] ?? opt.yearOptions?.[opt.yearOptions.length - 1] ?? null)
                      : null,
                  })
                }}>
                <option value="">None</option>
                {METRIC_GROUP_ORDER.map(group => {
                  const opts = group === 'union'
                    ? UNION_LAYER_OPTIONS
                    : pickerOptions.filter(o => o.group === group)
                  if (opts.length === 0) return null
                  return (
                    <optgroup
                      key={group}
                      label={METRIC_GROUP_LABELS[group]}
                      disabled={group === 'union' && !liveSupported}
                    >
                      {opts.map(o => (
                        <option key={o.key} value={o.key}>{o.label}</option>
                      ))}
                    </optgroup>
                  )
                })}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
            {!liveSupported && (
              <Text fontSize="xs" color="gray.500" mt={1}>
                Union &amp; “my universe” layers need precinct, tract, county or district geography.
              </Text>
            )}
            {option && !value.useUniverse && (
              <Text fontSize="xs" color="gray.500" mt={1}>{option.description}</Text>
            )}
          </Field.Root>

          {option?.requiresYear && (
            optionYears.length > 1 ? (
              <Field.Root>
                <Field.Label fontSize="xs" mb={1}>Election year: {value.year}</Field.Label>
                <Slider.Root
                  width="full"
                  px="2"
                  aria-label={["Election year"]}
                  min={0}
                  max={optionYears.length - 1}
                  step={1}
                  value={[Math.max(0, optionYears.indexOf(value.year ?? optionYears[optionYears.length - 1]))]}
                  onValueChange={(details: SliderValueChangeDetails) => onChange({ ...value, year: optionYears[details.value[0]] })}
                >
                  <Slider.Control>
                    <Slider.Track><Slider.Range /></Slider.Track>
                    <Slider.Thumb index={0} boxSize={5}>
                      <Slider.HiddenInput />
                    </Slider.Thumb>
                  </Slider.Control>
                </Slider.Root>
                <Flex justify="space-between">
                  <Text fontSize="xs" color="gray.500">{optionYears[0]}</Text>
                  <Text fontSize="xs" color="gray.500">{optionYears[optionYears.length - 1]}</Text>
                </Flex>
              </Field.Root>
            ) : (
              <Field.Root>
                <Field.Label fontSize="xs" mb={1}>Election year</Field.Label>
                <NativeSelect.Root size="sm">
                  <NativeSelect.Field
                    aria-label="Election year"
                    value={value.year ?? ''}
                    onChange={e => onChange({ ...value, year: Number(e.target.value) })}>
                    {optionYears.map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </Field.Root>
            )
          )}

          {value.mode === 'single' && value.metricOptionKey === 'lean' && (
            <Field.Root>
              <Flex align="center">
                <Switch.Root
                  id="swing-toggle"
                  size="sm"
                  checked={value.swingEnabled}
                  disabled={availableLeanYears.length < 2}
                  onCheckedChange={e => onChange({
                    ...value,
                    swingEnabled: e.checked === true,
                    swingFromYear: value.swingFromYear ?? availableLeanYears[availableLeanYears.length - 2] ?? null,
                    swingToYear: value.swingToYear ?? availableLeanYears[availableLeanYears.length - 1] ?? null,
                  })}
                  mr={2}
                >
                  <Switch.HiddenInput />
                  <Switch.Control>
                    <Switch.Thumb />
                  </Switch.Control>
                </Switch.Root>
                <Field.Label htmlFor="swing-toggle" fontSize="xs" mb={0}>Show swing arrows</Field.Label>
              </Flex>
              {availableLeanYears.length < 2 && (
                <Text fontSize="xs" color="gray.400">Swing needs two election cycles of lean data.</Text>
              )}
              {value.swingEnabled && availableLeanYears.length >= 2 && (
                <HStack mt={1} gap={2}>
                  <NativeSelect.Root size="xs">
                    <NativeSelect.Field
                      aria-label="Swing from year"
                      value={value.swingFromYear ?? ''}
                      onChange={e => onChange({ ...value, swingFromYear: Number(e.target.value) })}>
                      {availableLeanYears.map(y => <option key={y} value={y}>{y}</option>)}
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                  <Text fontSize="xs">→</Text>
                  <NativeSelect.Root size="xs">
                    <NativeSelect.Field
                      aria-label="Swing to year"
                      value={value.swingToYear ?? ''}
                      onChange={e => onChange({ ...value, swingToYear: Number(e.target.value) })}>
                      {availableLeanYears.map(y => <option key={y} value={y}>{y}</option>)}
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                </HStack>
              )}
            </Field.Root>
          )}

          {!value.useUniverse && option?.group === 'union' && (
            <Text fontSize="xs" color="gray.500">
              Shows your committee's tracked members only — sparse areas may still have union presence.
            </Text>
          )}

          {option && (
            <>
              <Field.Root>
                <Field.Label fontSize="xs" mb={1}>Layer opacity: {Math.round(value.opacity * 100)}%</Field.Label>
                {/* width="full" + px: see CompareLayersPanel (DT-10 collapsed-track fix). */}
                <Slider.Root
                  width="full"
                  px="2"
                  aria-label={["Layer opacity"]}
                  min={0.1}
                  max={1}
                  step={0.05}
                  value={[value.opacity]}
                  onValueChange={(details: SliderValueChangeDetails) => onChange({ ...value, opacity: details.value[0] })}
                >
                  <Slider.Control>
                    <Slider.Track><Slider.Range /></Slider.Track>
                    <Slider.Thumb index={0}>
                      <Slider.HiddenInput />
                    </Slider.Thumb>
                  </Slider.Control>
                </Slider.Root>
              </Field.Root>
              <Legend option={option} />
            </>
          )}
        </>
      )}
    </VStack>
  );
}

export function BivariateLegend() {
  return (
    <Box data-testid="bivariate-legend">
      <Text fontSize="xs" color="muted" mb={1}>Lean → · Turnout ↑</Text>
      {[2, 1, 0].map(row => (
        <Flex key={row} gap="2px" mb="2px">
          {[0, 1, 2].map(col => {
            const label = quadrantRationale(row as 0 | 1 | 2, col as 0 | 1 | 2)
            return (
              <Tooltip contentProps={{ fontSize: "xs" }} key={col} content={label} showArrow openDelay={0} positioning={{
                placement: "top"
              }}>
                <Box
                  data-cell={`${row}-${col}`}
                  aria-label={label}
                  tabIndex={0}
                  flex={1}
                  h="18px"
                  borderRadius="2px"
                  bg={BIVARIATE_PALETTE[row][col]}
                  cursor="default"
                />
              </Tooltip>
            );
          })}
        </Flex>
      ))}
      <Flex justify="space-between" mt={1}>
        <Text fontSize="xs" color="muted">R</Text>
        <Text fontSize="xs" color="muted">EVEN</Text>
        <Text fontSize="xs" color="muted">D</Text>
      </Flex>
      <Text fontSize="xs" color="muted" mt={1}>Hover or tap a cell for its strategy.</Text>
    </Box>
  );
}

function Legend({ option }: { option: MetricOption }) {
  return <MetricLegend option={option} domain={option.domain} />
}
